/*
 * aricove-components.js — de zeven eigen PDP-modules, uit de inline <script>-blokken
 * gehaald (P2, 26-08-2026). Eén asset, `defer`, ná `aricove-pdp-c-variant.js`.
 *
 * 🟥 DRIE REGELS DIE HIER GELDEN, EN WAAROM.
 *
 * 1. NUL LIQUID. In dit bestand staat geen enkele Liquid-tag — geen uitvoer-tag en geen
 *    zeggingstag; het is een statisch asset van de CDN en Liquid wordt er niet doorheen
 *    gedraaid. (Ook dit commentaar bevat er geen: de poort telt met een regex over het hele
 *    bestand, dus een voorbeeld in proza zou zijn eigen meting bederven.) Alles wat de server
 *    moet aanleveren komt via een `data-`-attribuut op het element. Precies één module
 *    gebruikte Liquid in zijn scriptbody — `layers`, met het sectie-id en de twee
 *    overzichtsteksten. Die drie staan nu op de DOM: het sectie-id stond altijd al op
 *    `[data-lx-root]` (JS leest het nu daar uit in plaats van het ingebakken te krijgen),
 *    en de twee teksten staan als `data-lx-cap-name`/`data-lx-cap-sub` op
 *    `[data-lx-caption]`, hetzelfde element dat ze ook zichtbaar rendert. Zelfde
 *    Liquid-variabelen, zelfde render-moment, alleen een andere doorgeefweg.
 *
 * 2. DE GUARDS BLIJVEN, MET DEZELFDE NAMEN. `__apdpDrawerBound`, `__apdpcSheetBound`,
 *    `__apdpcTabsBound`, `__apdpCartBound`, `__apdpVideoBound` worden hieronder gezet
 *    zoals ze altijd gezet werden. Ze zijn niet decoratief: het snippet kon twee keer op
 *    één pagina staan en dan bond het twee keer. Dat is de bug die deze vlaggen hebben
 *    afgekocht. Ze bewaken nu de EENMALIGE helft van elke module (de opbouw en de
 *    gedelegeerde document-listeners); de element-bindingen zitten in een aparte, idempotente
 *    functie die een herbinding wél opnieuw mag draaien.
 *
 * 3. HERBINDEN OP `shopify:section:load`. In de Theme Editor vervangt Shopify één sectie
 *    zonder paginarefresh. Alles wat aan een element in díe sectie hing, hangt daarna aan een
 *    knoop die uit de DOM is. Zonder herbinding is de sectie in de editor dood — in productie
 *    merkt niemand het, Wilson wel. Elke module registreert daarom een herbinder; die is
 *    idempotent gemaakt met een `…-bound`-attribuut op de wortel, zodat een herbinding nooit
 *    een tweede listenerset oplevert.
 *
 * 🟥 EÉN BESTAND = EÉN GLOBAL SCOPE. De zeven blokken hadden overlappende namen op
 *    IIFE-niveau (`root`, `esc`, `open`, `close`, `lastFocus`, `key`, `label`,
 *    `btn`, `focusables`). Elke module houdt daarom zijn eigen functiescope; er wordt
 *    hieronder niets naar de buitenste scope getild. De enige gedeelde namen zijn de
 *    `window.__apdp*`-guards, en die waren al gedeeld.
 *
 * ES5-compatibel, geen modules, geen optional chaining — gelijk aan wat er inline stond.
 */
(function () {
  'use strict';

  /* Registers van herbinders; `shopify:section:load` draait ze allemaal opnieuw. */
  var herbinders = [];

  /**
   * Registreer een module.
   * @param {string} guard   naam van de window-vlag die de EENMALIGE helft bewaakt
   * @param {Function} eenmalig  opbouw + gedelegeerde document-listeners (max één keer)
   * @param {Function} [herbind] element-bindingen; mag vaker draaien, moet idempotent zijn
   */
  function module(guard, eenmalig, herbind) {
    if (!window[guard]) {
      window[guard] = true;
      try { eenmalig(); } catch (e) { if (window.console) console.warn('[apdp] ' + guard + ': ' + e.message); }
    }
    if (herbind) {
      herbinders.push(herbind);
      try { herbind(); } catch (e) { if (window.console) console.warn('[apdp] ' + guard + ' herbind: ' + e.message); }
    }
  }

  /* ==================================================================
     DRAWER — snippets/aricove-pdp-cart-drawer.liquid
     ================================================================== */
  module('__apdpDrawerBound', function () {
    var bindElementen = null;
      var root = document.querySelector('[data-apdp-drawer-root]');
      if(!root) return;
      var drawer = root.querySelector('[data-apdp-drawer]');
      var backdrop = root.querySelector('[data-apdp-drawer-backdrop]');
      var itemsEl = root.querySelector('[data-apdp-items]');
      var elSubtotal = root.querySelector('[data-apdp-subtotal]');
      var savingsEl = root.querySelector('[data-apdp-savings]');
      var savingsWas = root.querySelector('[data-apdp-savings-was]');
      var savingsAmt = root.querySelector('[data-apdp-savings-amt]');
      var upsell = root.querySelector('[data-apdp-upsell]');
      var calloutEl = root.querySelector('[data-apdp-callout]');
      var money = function(c){ return '$' + (c/100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','); };
      var esc = function(s){ return String(s == null ? '' : s).replace(/[&<>"']/g, function(m){ return ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'})[m]; }); };
      var lastFocus = null;
      var focusTimer = null;
      var addedKey = null;          // cart line key of the just-added item (highlight + scroll)
      var busy = false;             // guard against double qty/remove clicks mid-request

      // Map known Aricove colour names to a CSS colour for the swatch; unknown colours fall
      // back to an outlined neutral circle (matches the comp's "empty circle = placeholder").
      var COLOR_HEX = {
        'natural':'#e9e1d2','sand':'#d8c7a8','dune':'#cdb79a','oat':'#e3d8c2','ivory':'#f3ecdd',
        'cream':'#efe7d4','stone':'#cfc9bd','grey':'#9aa0a6','gray':'#9aa0a6','charcoal':'#3c3f44',
        'slate':'#5b6470','navy':'#1f2f4d','blue':'#3a5a86','sky':'#9cc0e0','sage':'#9bab8a',
        'green':'#5f7a52','blush':'#e6c8c0','rose':'#d9a7a3','pink':'#e7b8c6','white':'#ffffff',
        'black':'#16181c','brown':'#7a5a3c','taupe':'#b7a892','terracotta':'#c1664a','rust':'#a9542f'
      };
      function colorHex(value){
        if(!value) return null;
        var key = String(value).toLowerCase().trim();
        if(COLOR_HEX[key]) return COLOR_HEX[key];
        // try the last word (e.g. "Warm Sand" -> "sand")
        var parts = key.split(/[\s\/-]+/);
        for(var i = parts.length - 1; i >= 0; i--){ if(COLOR_HEX[parts[i]]) return COLOR_HEX[parts[i]]; }
        return null;
      }

      // Friendly WB size label: prefix the size name when the variant leads with a known
      // dimension ("60\"x80\" 15lb" -> "Full 60\"x80\" 15 lb"). Product-neutral pass-through.
      var WB_SIZES = { '48"x72"':'Twin', '60"x80"':'Full', '80"x87"':'Queen', '88"x104"':'King' };
      function prettyVariant(t){
        if(!t || t === 'Default Title') return '';
        for(var dim in WB_SIZES){ if(t.indexOf(dim) === 0){ return WB_SIZES[dim] + ' ' + t.replace(/(\d)\s*lb/i, '$1 lb'); } }
        return t.replace(/(\d)\s*lb/i, '$1 lb');
      }

      // Build the variant line: a colour swatch (when a colour option exists) + the full
      // option string. Falls back to the prettified variant_title.
      function variantMarkup(it){
        // Drop placeholder/Default options so single-variant products show no variant line.
        var opts = (it.options_with_values || []).filter(function(o){ return o.value && o.value !== 'Default Title'; });
        var swatch = '', colorVal = null;
        for(var i=0;i<opts.length;i++){ if(/colou?r/i.test(opts[i].name)){ colorVal = opts[i].value; break; } }
        if(colorVal){
          var hex = colorHex(colorVal);
          swatch = hex
            ? '<span class="drawer-item__swatch" style="background:'+hex+'" title="'+esc(colorVal)+'" aria-hidden="true"></span>'
            : '<span class="drawer-item__swatch drawer-item__swatch--empty" title="'+esc(colorVal)+'" aria-hidden="true"></span>';
        }
        var label;
        if(opts.length){
          label = opts.map(function(o){ return o.value; }).join(' · ');
          // keep the friendly "lb" spacing if a weight slipped into an option value
          label = label.replace(/(\d)\s*lb/i, '$1 lb');
        } else {
          label = prettyVariant(it.variant_title || '');
        }
        if(!label && !swatch) return '';
        return '<div class="drawer-item__variant">'+swatch+'<span>'+esc(label)+'</span></div>';
      }

      // BMDC 2026-07-01 (#24): /cart.js line items don't carry compare_at_price, so an on-sale
      // (compare-at markdown) variant showed only the sale price with no struck "was". Fetch the
      // per-variant compare_at_price from /products/{handle}.js (cached per product) and use it.
      // Distinct from automatic cart discounts (D-0021), which surface at checkout, not per line.
      var compareAtByVariant = {};                 // variantId -> compare_at_price (cents)
      var compareAtFetched = {};                   // handle -> true (in-flight/done)
      function fetchCompareAtForItems(items){
        var handles = {};
        items.forEach(function(it){ if(it.handle && !compareAtFetched[it.handle]) handles[it.handle] = true; });
        var toFetch = Object.keys(handles);
        toFetch.forEach(function(h){ compareAtFetched[h] = true; });
        return Promise.all(toFetch.map(function(h){
          return fetch('/products/'+encodeURIComponent(h)+'.js', { headers: { 'Accept': 'application/json' } })
            .then(function(r){ return r.ok ? r.json() : null; })
            .then(function(p){ if(p && p.variants){ p.variants.forEach(function(v){ compareAtByVariant[v.id] = v.compare_at_price || 0; }); } })
            .catch(function(){ /* leave uncached; line just shows the sale price */ });
        }));
      }
      function patchWasPrices(items){
        if(!itemsEl) return;
        var byKey = {}; items.forEach(function(it){ byKey[it.key] = it; });
        itemsEl.querySelectorAll('.drawer-item[data-key]').forEach(function(el){
          var it = byKey[el.getAttribute('data-key')]; if(!it) return;
          var listed = it.original_price != null ? it.original_price : it.price;
          var fin = it.final_price != null ? it.final_price : listed;
          if(fin < listed) return; // line discount already struck the listed price (takes precedence)
          var cmp = compareAtByVariant[it.variant_id || it.id] || 0;
          var priceEl = el.querySelector('.drawer-item__price');
          if(cmp && cmp > listed && priceEl && !priceEl.querySelector('.was')){
            priceEl.insertAdjacentHTML('afterbegin', '<span class="was">'+money(cmp)+'</span> ');
          }
        });
      }

      function lineHTML(it){
        var src = (it.featured_image && it.featured_image.url) || it.image || '';
        // Wilson 07-02: line discount (final_price < original_price) wins — show the discounted
        // price with the listed price struck; else fall back to the compare-at "was" (#24).
        var orig = it.original_price != null ? it.original_price : it.price;
        var fin = it.final_price != null ? it.final_price : orig;
        var shown = orig, wasHTML = '';
        if(fin < orig){
          shown = fin;
          wasHTML = '<span class="was">'+money(orig)+'</span> ';
        } else {
          // Prefer the cached compare-at (from /products/*.js); fall back to any cart-provided value.
          var cmp = compareAtByVariant[it.variant_id || it.id] || it.compare_at_price || 0;
          if(cmp && cmp > orig) wasHTML = '<span class="was">'+money(cmp)+'</span> ';
        }
        var added = (it.key === addedKey) ? ' drawer-item--added' : '';
        var img = src
          ? '<img src="'+esc(src)+'" alt="'+esc(it.product_title || it.title || '')+'" width="72" height="72" loading="lazy">'
          : '';
        return ''+
          '<div class="drawer-item'+added+'" data-apdp-line data-key="'+esc(it.key)+'">'+
            '<div class="drawer-item__img"><a href="'+esc(it.url || '#')+'">'+img+'</a></div>'+
            '<div class="drawer-item__main">'+
              '<div class="drawer-item__titlerow">'+
                '<a class="drawer-item__title" href="'+esc(it.url || '#')+'">'+esc(it.product_title || it.title || '')+'</a>'+
                '<button type="button" class="drawer-item__remove" data-apdp-remove data-key="'+esc(it.key)+'" aria-label="Remove '+esc(it.product_title || it.title || 'item')+'">'+
                  '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6m5 4v6m4-6v6"/></svg>'+
                '</button>'+
              '</div>'+
              variantMarkup(it)+
              '<div class="drawer-item__row">'+
                '<div class="drawer-item__price">'+wasHTML+'<span>'+money(shown)+'</span></div>'+
                '<div class="qty-stepper" data-apdp-qty data-key="'+esc(it.key)+'">'+
                  '<button type="button" class="qty-stepper__btn" data-apdp-qty-dec data-key="'+esc(it.key)+'" aria-label="Decrease quantity">&minus;</button>'+
                  '<span class="qty-stepper__val">'+(it.quantity || 1)+'</span>'+
                  '<button type="button" class="qty-stepper__btn" data-apdp-qty-inc data-key="'+esc(it.key)+'" aria-label="Increase quantity">+</button>'+
                '</div>'+
              '</div>'+
            '</div>'+
          '</div>';
      }

      function renderItems(cart){
        if(!itemsEl || !cart) return;
        var items = cart.items || [];
        if(!items.length){
          itemsEl.innerHTML = '<div class="drawer-empty">'
            + '<svg class="drawer-empty__ico" viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>'
            + '<p class="drawer-empty__title">Your cart is empty</p>'
            + '<a class="drawer-empty__btn" href="/collections/all" data-apdp-drawer-close>Shop best-sellers</a>'
            + '</div>';
        } else {
          itemsEl.innerHTML = items.map(lineHTML).join('');
          // #24: pull compare-at prices (cached), then patch the struck "was" onto any on-sale lines.
          // First paint uses whatever's already cached (no flash on re-render); the fetch fills the rest.
          fetchCompareAtForItems(items).then(function(){ patchWasPrices(items); });
          // Scroll the just-added line into view inside the drawer body.
          var addedEl = itemsEl.querySelector('.drawer-item--added');
          if(addedEl && addedEl.scrollIntoView){ try { addedEl.scrollIntoView({ block: 'nearest' }); } catch(e){} }
        }
        // BMDC 2026-06-27 (overrides D-0021): Subtotal reflects the DISCOUNT — show what the customer
        // actually pays (cart.total_price, after applied/automatic cart discounts), not the listed total.
        var listedTotal = cart.original_total_price != null ? cart.original_total_price : cart.total_price;
        var payTotal = cart.total_price != null ? cart.total_price : listedTotal;
        if(elSubtotal) elSubtotal.textContent = money(payTotal);
        // Savings (C4): shown when the live cart carries a discount (applied code or automatic).
        var saved = (cart.total_discount || 0);
        if(!saved && cart.original_total_price != null && cart.total_price != null){ saved = cart.original_total_price - cart.total_price; }
        if(savingsEl){
          if(saved > 0){
            if(savingsWas) savingsWas.textContent = money(listedTotal);
            if(savingsAmt) savingsAmt.textContent = '−' + money(saved);
            savingsEl.hidden = false;
          } else { savingsEl.hidden = true; }
        }
        // Free-shipping callout: hide when cart is empty, show when items are present.
        if(calloutEl) calloutEl.hidden = !items.length;
        // Header cart bubble.
        var n = (cart && typeof cart.item_count === 'number') ? cart.item_count : null;
        document.querySelectorAll('.cart-link__bubble').forEach(function(b){
          if(n !== null) b.textContent = n;
          b.classList.add('cart-link__bubble--visible');
        });
        // Drawer title count "Your Cart (n)".
        var cnt = root.querySelector('[data-apdp-count]');
        if(cnt && n !== null) cnt.textContent = '(' + n + ')';
        // Hide upsell cards for any product already in the cart.
        if(upsell){
          var upHead = upsell.querySelector('.drawer-upsell__head');
          if(upHead) upHead.textContent = items.length ? 'Pairs well with' : 'Most loved';
          var inCart = {};
          items.forEach(function(it){ var h = it.handle || (it.url ? it.url.split('?')[0].split('/').pop() : ''); if(h) inCart[h] = true; });
          var any = false;
          upsell.querySelectorAll('[data-apdp-upsell-card]').forEach(function(card){
            var hide = inCart[card.getAttribute('data-upsell-handle')];
            card.hidden = !!hide;
            if(!hide) any = true;
          });
          upsell.hidden = !any;
        }
      }

      function refreshCart(){
        return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
          .then(function(r){ return r.json(); })
          .then(function(c){ renderItems(c); return c; })
          .catch(function(){
            document.querySelectorAll('.cart-link__bubble').forEach(function(b){ b.classList.add('cart-link__bubble--visible'); });
          });
      }

      function changeQty(key, q, addedQuantity){
        if(!key || q < 0 || busy) return;
        busy = true;
        fetch('/cart/change.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: key, quantity: q })
        }).then(function(r){
          if(!r.ok) throw new Error('cart change failed: ' + r.status);
          return r.json();
        }).then(function(c){
          if(addedQuantity > 0 && c && c.items){
            var line = c.items.filter(function(item){ return item.key === key; })[0];
            if(line){
              document.dispatchEvent(new CustomEvent('aricove:cart-add-succeeded', {
                detail: { response: line, quantity: addedQuantity }
              }));
            }
          }
          renderItems(c);
        }).then(function(){ busy = false; })
          .catch(function(){ busy = false; refreshCart(); });
      }

      function focusables(){ return drawer.querySelectorAll('button, [href], input, [tabindex]:not([tabindex="-1"])'); }

      function open(){
        lastFocus = document.activeElement;
        /* Forced reflow rather than rAF — rAF does not fire in a background tab, which left the
           drawer unhidden but stuck at opacity:0. Same fix as the cover modal (ad902a6). */
        drawer.hidden = false; void drawer.offsetWidth;
        drawer.classList.add('is-open'); backdrop.classList.add('is-open');
        document.body.style.overflow = 'hidden';
        var c = drawer.querySelector('[data-apdp-drawer-close]');
        if(focusTimer) clearTimeout(focusTimer);
        if(c) focusTimer = setTimeout(function(){ focusTimer = null; c.focus(); }, 60);
      }
      function close(){
        if(focusTimer){ clearTimeout(focusTimer); focusTimer = null; }
        drawer.classList.remove('is-open'); backdrop.classList.remove('is-open');
        document.body.style.overflow = '';
        setTimeout(function(){ drawer.hidden = true; }, 360);
        if(lastFocus && lastFocus.focus) lastFocus.focus();
      }

      // Open on a successful add. detail = the /cart/add.js response (its .items[0] is the line).
      document.addEventListener('apdp:added', function(e){
        var d = e.detail || {};
        var it = (d.items && d.items[0]) || d;
        addedKey = it && it.key ? it.key : null;
        refreshCart(); open();
      });

      // Open on demand from the header cart icon (Wilson 06-25 Track 6) — no "just added" highlight.
      document.addEventListener('apdp:cart:open', function(){
        addedKey = null;
        refreshCart(); open();
      });

      // Delegated controls on the line list: qty steppers + remove.
      if(itemsEl) itemsEl.addEventListener('click', function(e){
        var dec = e.target.closest('[data-apdp-qty-dec]');
        var inc = e.target.closest('[data-apdp-qty-inc]');
        var rm = e.target.closest('[data-apdp-remove]');
        if(rm){ changeQty(rm.getAttribute('data-key'), 0); return; }
        if(dec || inc){
          var btn = dec || inc;
          var key = btn.getAttribute('data-key');
          var valEl = btn.parentNode.querySelector('.qty-stepper__val');
          var cur = valEl ? parseInt(valEl.textContent, 10) || 1 : 1;
          var next = dec ? cur - 1 : cur + 1;
          if(next < 1){ changeQty(key, 0, 0); } else { changeQty(key, next, inc ? 1 : 0); }
        }
      });

      // Upsell variant selector -> keep the displayed price in sync with the chosen variant.

    /* P2: de element-bindingen apart, zodat een section:load ze opnieuw kan leggen.
       De drawer-wortel zelf staat in de header (buiten de vervangen sectie), dus in de
       praktijk raakt hij zelden weg — maar de upsell-kaarten worden wél opnieuw gerenderd.

       🟥 IDEMPOTENT, EN DAT IS HIER GEEN NETHEID MAAR EEN KOOPADBUG.
       Deze functie draait bij ELKE `shopify:section:load`. `addEventListener` op een
       element dat er nog stond, stapelt dan een TWEEDE handler op dezelfde knoop. Op
       `[data-apdp-upsell-add]` betekent dat: één klik, twee `/cart/add.js`-verzoeken,
       twee regels in de mand van de klant. Elke knoop krijgt daarom een merkteken en
       wordt overgeslagen als hij al gebonden is; alleen knopen die de section-load echt
       heeft vervangen (die dragen het merkteken niet) worden opnieuw gebonden. */
    var gebonden = function (el) {
      if (!el) return true;
      /* Property, geen attribuut — innerHTML kopieert attributen mee en zou een verse
         knoop ten onrechte als gebonden aanmerken. Zie de toelichting bij layers. */
      if (el.__apdpDrawerBound) return true;
      el.__apdpDrawerBound = true;
      return false;
    };
    bindElementen = function () {
        root.querySelectorAll('[data-apdp-upsell-variant]').forEach(function(sel){
          if (gebonden(sel)) return;
          sel.addEventListener('change', function(){
            var card = sel.closest('[data-apdp-upsell-card]'); if(!card) return;
            var opt = sel.options[sel.selectedIndex];
            var priceEl = card.querySelector('[data-apdp-upsell-price]');
            if(priceEl && opt && opt.getAttribute('data-price')) priceEl.textContent = opt.getAttribute('data-price');
            // Wilson 06-30: swap the thumbnail to the chosen variant's image (data-img on the option).
            var imgEl = card.querySelector('[data-apdp-upsell-img]');
            var newImg = opt && opt.getAttribute('data-img');
            if(imgEl && newImg) imgEl.setAttribute('src', newImg);
            var addBtn = card.querySelector('[data-apdp-upsell-add]');
            if(addBtn){
              var sold = opt && opt.disabled;
              addBtn.disabled = !!sold;
              addBtn.textContent = sold ? 'Sold out' : 'Add';
            }
          });
        });

        // Upsell add -> /cart/add.js, then refresh the drawer with the new line
        root.querySelectorAll('[data-apdp-upsell-add]').forEach(function(btn){
          if (gebonden(btn)) return;
          btn.addEventListener('click', function(){
            var card = btn.closest('[data-apdp-upsell-card]');
            var sel = card && card.querySelector('[data-apdp-upsell-variant]');
            var id = (sel && sel.value) || btn.getAttribute('data-variant-id'); if(!id) return;
            btn.disabled = true; var prev = btn.textContent; btn.textContent = 'Adding…';
            fetch('/cart/add.js', {
              method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ items: [{ id: id, quantity: 1 }] })
            }).then(function(r){
              if(!r.ok) throw new Error('upsell add failed: ' + r.status);
              return r.json();
            }).then(function(res){
              document.dispatchEvent(new CustomEvent('aricove:cart-add-succeeded', {
                detail: { response: res }
              }));
              var it = (res.items && res.items[0]) || res;
              addedKey = it && it.key ? it.key : null;
              return refreshCart();
            }).then(function(){ btn.disabled = false; btn.textContent = prev; })
              .catch(function(){ btn.disabled = false; btn.textContent = prev; });
          });
        });

        // (Promo-code field removed from the drawer — Wilson 06-27; the discount field lives on checkout.)

        if (!gebonden(backdrop)) backdrop.addEventListener('click', close);
        root.querySelectorAll('[data-apdp-drawer-close]').forEach(function(b){ if (!gebonden(b)) b.addEventListener('click', close); });
    };
    bindElementen();
    document.addEventListener('apdp:drawer:herbind', bindElementen);

      document.addEventListener('keydown', function(e){
        if(drawer.hidden) return;
        if(e.key === 'Escape'){ close(); return; }
        if(e.key === 'Tab'){
          var f = focusables(); if(!f.length) return;
          var first = f[0], last = f[f.length-1];
          if(e.shiftKey && document.activeElement === first){ e.preventDefault(); last.focus(); }
          else if(!e.shiftKey && document.activeElement === last){ e.preventDefault(); first.focus(); }
        }
      });
  }, function () {
    /* Herbinding: de opbouw is al gedaan, alleen de element-listeners opnieuw. */
    document.dispatchEvent(new CustomEvent('apdp:drawer:herbind'));
  });

  /* ==================================================================
     LAYERS — sections/aricove-pdp-layers.liquid
     Enige module die Liquid in zijn body had; zie regel 1 bovenaan.
     Bindt per wortel, want een pagina kan meerdere layers-secties dragen.
     ================================================================== */
  function bindLayers() {
    var wortels = document.querySelectorAll('[data-lx-root]');
    for (var w = 0; w < wortels.length; w++) {
      (function (root) {
            /* P2: de sectie-id kwam uit Liquid in de scriptbody. Nu bindt de module per
               wortel die de server rendert; het id staat nog steeds op het attribuut,
               maar JS leest het uit de DOM in plaats van het ingebakken te krijgen. */
            if (!root) return;
            /* 🟥 HET MERKTEKEN MAG GEEN ATTRIBUUT ZIJN, EN DAT IS GEMETEN, NIET BEDACHT.
               De Theme Editor vervangt een sectie via innerHTML. Een ATTRIBUUT overleeft dat
               kopiëren: de nieuwe, nog volstrekt ONGEBONDEN knoop draagt `data-lx-bound` al,
               de module slaat hem over, en de accordeon is in de editor dood. Precies dat
               gebeurde in de eerste versie hiervan — `meet-p2-herbinding.mjs` sloeg er rood
               op (layers GROEN vóór, ROOD ná de herbinding).
               Een property op het DOM-object zelf wordt NIET meegekopieerd door innerHTML: de
               verse knoop is een ander object en heeft hem dus niet. Daarmee is het merkteken
               waar op gestuurd wordt ("is DEZE knoop gebonden") weer gelijk aan wat het moet
               betekenen. */
            if (root.__lxBound) return;
            root.__lxBound = true;

            var stage   = root.querySelector('[data-lx-stage]');
            var map     = root.querySelector('[data-lx-map]');
            var reset   = root.querySelector('[data-lx-reset]');
            var caption = root.querySelector('[data-lx-caption]');
            var macros  = Array.prototype.slice.call(root.querySelectorAll('[data-lx-macro]'));
            var pins    = Array.prototype.slice.call(root.querySelectorAll('[data-lx-pin]'));
            var rows    = Array.prototype.slice.call(root.querySelectorAll('[data-lx-row]'));
            var mats    = Array.prototype.slice.call(root.querySelectorAll('[data-lx-mat]'));
            var bodies  = Array.prototype.slice.call(root.querySelectorAll('[data-lx-body]'));
            /* BMDC 2026-08-05: the stage is optional now (no cover art yet), so only the rows are
               required — the accordion still works with no stage to drive. */
            if (!rows.length) return;

            /* P2: de twee overzichtsteksten stonden als Liquid IN de scriptbody. Ze staan
               nu op [data-lx-caption] als data-attributen — dezelfde Liquid-variabelen,
               op hetzelfde render-moment, alleen via de DOM. De server rendert de tekst
               ook zichtbaar in de caption zelf, dus er is geen tweede bron. */
            var OVERVIEW_NAME = (caption && caption.getAttribute('data-lx-cap-name')) || '';
            var OVERVIEW_SUB  = (caption && caption.getAttribute('data-lx-cap-sub')) || '';

            var active = -1;   /* -1 = overview, 0..3 = focused material */

            function esc(s) {
              return String(s == null ? '' : s)
                .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
            }

            function render() {
              var on  = active >= 0;
              var row = on ? rows[active] : null;

              if (stage) stage.classList.toggle('is-focus', on);

              /* 1. map pushes toward this layer's focus point, then fades out */
              if (map) {
                map.style.transform = on
                  ? 'scale(' + parseFloat(row.getAttribute('data-scale')) + ') translate(' +
                    ((0.5 - parseFloat(row.getAttribute('data-fx'))) * 100).toFixed(2) + '%,' +
                    ((0.5 - parseFloat(row.getAttribute('data-fy'))) * 100).toFixed(2) + '%)'
                  : 'scale(1) translate(0%,0%)';
              }

              /* 2. macro cross-fade — keyed on data-lx-macro, not array position: a set may leave
                    individual macros unrendered (no asset yet), which would shift the indexes. */
              macros.forEach(function (el) {
                el.classList.toggle('is-on', parseInt(el.getAttribute('data-lx-macro'), 10) === active);
              });

              /* 3. accordion + active thumb ring */
              mats.forEach(function (el, i) {
                el.classList.toggle('is-open', i === active);
                el.classList.toggle('is-active', i === active);
              });
              rows.forEach(function (el, i) {
                el.setAttribute('aria-expanded', i === active ? 'true' : 'false');
              });
              bodies.forEach(function (el, i) {
                el.setAttribute('aria-hidden', i === active ? 'false' : 'true');
              });

              /* 4. caption */
              if (caption) {
                caption.innerHTML = on
                  ? '<span class="lx__cap-name">' + esc(row.getAttribute('data-name')) + '</span>' +
                    '<span class="lx__cap-sub"><span class="lx__cap-hint">' + row.getAttribute('data-loc') +
                    '</span> · ' + esc(row.getAttribute('data-hint')) + '</span>'
                  : '<span class="lx__cap-name">' + OVERVIEW_NAME + '</span>' +
                    '<span class="lx__cap-sub">' + OVERVIEW_SUB + '</span>';
              }
            }

            function select(i) { active = i; render(); }
            function clear()   { active = -1; render(); }

            pins.forEach(function (p, i) { p.addEventListener('click', function () { select(i); }); });
            rows.forEach(function (r, i) {
              r.addEventListener('click', function () {
                select(active === i ? -1 : i);
              });
            });
            if (reset) reset.addEventListener('click', clear);

            /* Keyboard: while a material is active, arrows cycle with wraparound and Escape
               restores the overview. The same interaction now applies at every viewport. */
            document.addEventListener('keydown', function (e) {
              if (active < 0) return;
              if (e.key === 'ArrowRight') { select((active + 1) % rows.length); e.preventDefault(); }
              else if (e.key === 'ArrowLeft') { select((active - 1 + rows.length) % rows.length); e.preventDefault(); }
              else if (e.key === 'Escape') { clear(); }
            });

            render();
      })(wortels[w]);
    }
  }
  module('__apdpLayersBound', function () { /* geen eenmalige opbouw */ }, bindLayers);

  /* ==================================================================
     SHEET — snippets/aricove-pdp-c-sheet.liquid
     Volledig gedelegeerd op document; herbinding niet nodig, wél als de
     wortel bij de eerste poging nog ontbrak.
     ================================================================== */
  module('__apdpcSheetBound', function () {
      'use strict';
      var root      = document.querySelector('[data-apdpc-sheet-root]');
      var scrim     = document.querySelector('[data-apdpc-scrim]');
      if (!root || !scrim) return;

      /* Sleutels die géén .apdpc-sheet zijn maar een eigen overlay (R7).
       * Waarde = het attribuut waarop die overlay zijn eigen trigger bindt. */
      var PASSTHROUGH = { 'quiz': 'data-apdp-modal' };

      /* Laatste trigger-element: focus wordt hier naartoe teruggezet na sluiten.
       * Ontbrak in de mobiele sheets -- dat is de a11y-regressie die we hier oplossen. */
      var lastFocus = null;
      /* Huidig open paneel (of null). */
      var activePanel = null;
      var focusTimer = null;

      /* Alle focusbare elementen in een paneel.
       * Selectoren spiegelen de bestaande aricove-pdp-modal.liquid focus-trap (r.144-145). */
      function focusables(panel) {
        return panel.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
      }

      /* Open een paneel op basis van de sleutel. */
      function openPanel(key) {
        var panel = root.querySelector('[data-apdpc-sheet="' + key + '"]');
        if (!panel) {
          var template = root.querySelector('template[data-apdpc-sheet-template="' + key + '"]');
          if (!template) return;
          template.replaceWith(template.content.cloneNode(true));
          panel = root.querySelector('[data-apdpc-sheet="' + key + '"]');
        }
        if (!panel) return;

        /* Vorig paneel sluiten zonder animatie zodat er nooit twee tegelijk open zijn. */
        if (activePanel && activePanel !== panel) {
          activePanel.classList.remove('is-open');
          activePanel.hidden = true;
        }
        activePanel = panel;

        /* Scrim aanzetten.
         * Waarom hidden=false VOOR is-open: spiegelt de reflow-truc in aricove-pdp-modal
         * r.157 zodat achtergrondtabs het niet vergrendeld laten staan op opacity:0. */
        scrim.hidden = false;
        void scrim.offsetWidth;
        scrim.classList.add('is-open');

        panel.hidden = false;
        void panel.offsetWidth;
        panel.classList.add('is-open');

        document.body.style.overflow = 'hidden';

        /* Focus naar de sluitknop na de transitie (spiegelt modal r.163-164).
         * 60 ms zodat de focus betrouwbaar landt als is-open de pointer-events inschakelt. */
        var closeBtn = panel.querySelector('[data-apdpc-sheet-close]');
        if (closeBtn) {
          clearTimeout(focusTimer);
          focusTimer = setTimeout(function () { if (activePanel === panel) closeBtn.focus(); }, 60);
        }
      }

      /* Sluit het actieve paneel en herstel focus. */
      function closePanel() {
        clearTimeout(focusTimer);
        if (!activePanel) return;

        scrim.classList.remove('is-open');
        activePanel.classList.remove('is-open');

        var panelRef = activePanel;
        activePanel = null;

        /* hidden pas ERNA zodat de CSS-transitie (360ms) zichtbaar is.
         * Spiegelt modal r.169 (hidden na 220ms); we nemen 380ms voor de springveertransitie. */
        setTimeout(function () {
          if (activePanel === panelRef) return;
          panelRef.hidden = true;
          if (!activePanel) scrim.hidden = true;
        }, 380);

        document.body.style.overflow = '';

        /* Focus terug naar de triggerende knop. Ontbrak in de mobiele sheets -- a11y-regressie. */
        if (lastFocus && typeof lastFocus.focus === 'function') {
          lastFocus.focus();
        }
        lastFocus = null;
      }

      /* Openen via event-delegatie: elk element met data-apdpc-sheet="<key>".
       * CONTRACT §4: "Openen: elk element met data-apdpc-sheet=<key>".
       *
       * 🟥 P1 tranche 1 (27-08) — `data-apdp-modal` komt hier ook binnen, en dat is
       * geen uitbreiding maar een OVERNAME. De copy in de FAQ-antwoorden draagt
       * `<button data-apdp-modal="zones-guide">` (bed) en `data-apdp-modal="weight-guide"`
       * / `"quiz"` (WB). Dat is Wilsons copy: het attribuut staat IN de tekst en mag
       * niet herschreven worden. Tot nu toe ving `snippets/aricove-pdp-bed-modal.liquid`
       * die klik met een eigen gedelegeerde listener; dat snippet vervalt met deze
       * tranche, dus de sheet-controller neemt die rol over.
       *
       * Alleen sleutels met een ECHT paneel worden afgevangen — anders zou een klik op
       * een `data-apdp-modal`-trigger zonder paneel hier stil blijven hangen in plaats
       * van door te lopen naar de overlay die hem wél kent (de quiz, die zijn eigen
       * gedelegeerde listener heeft). Die check gebeurt hieronder bij `panel`. */
      document.addEventListener('click', function (e) {
        if (!e.target.closest) return;
        var trigger = e.target.closest('[data-apdpc-sheet]');
        var key;
        if (trigger) {
          /* Sluit-knoppen (data-apdpc-sheet-close) hebben géén waarde-attribuut.
           * We herkennen ze hierdoor -- laat ze door de close-listener afhandelen. */
          key = trigger.getAttribute('data-apdpc-sheet');
        } else {
          var legacy = e.target.closest('[data-apdp-modal]');
          if (!legacy) return;
          var legacyKey = legacy.getAttribute('data-apdp-modal');
          /* Geen paneel voor deze sleutel? Niets doen en de klik met rust laten. */
          if (!legacyKey || !root.querySelector('[data-apdpc-sheet="' + legacyKey + '"], template[data-apdpc-sheet-template="' + legacyKey + '"]')) return;
          trigger = legacy;
          key = legacyKey;
        }
        if (!key || key === '') return;
        /* Voorkomen dat een sheet-knop BINNEN een open paneel een ander paneel opent
         * zonder dat de sluitlogica loopt. */
        if (trigger.hasAttribute('data-apdpc-sheet-close')) return;

        /* Passthrough-sleutels (R7): geen .apdpc-sheet, maar een eigen overlay met
         * eigen DOM, CSS, focus-trap en Escape. Nu alleen `quiz`.
         *
         * We sluiten een eventueel open paneel -- precies wat de mobiele build deed
         * toen de weight-guide naar de quiz doorgaf (aricove-pdp-m-hero.liquid
         * r.495-498) -- en laten de overlay het overnemen.
         *
         * Waarom een synthetische klik en geen directe aanroep: de quiz exporteert
         * geen open()-functie, hij bindt een gedelegeerde document-listener op
         * [data-apdp-modal="quiz"] (aricove-pdp-quiz.liquid r.746-748). Die is al
         * langs deze klik heen (closest() is bij dispatch geëvalueerd), dus het
         * attribuut alsnog zetten helpt niet meer. Een tweede klik op een verborgen
         * element mét dat attribuut bereikt zijn listener wél, zonder dat we de
         * open-logica hier dupliceren. Eén bron van waarheid.
         *
         * De 380ms wacht de sluit-transitie van closePanel() af, zodat de scrim van
         * het paneel niet over de quiz heen blijft liggen. */
        var passKey = PASSTHROUGH[key];
        if (passKey) {
          var proxy = document.querySelector('[data-apdpc-passthrough="' + key + '"]');
          if (proxy) {
            e.preventDefault();
            var hadPanel = !!activePanel;
            if (hadPanel) closePanel();
            setTimeout(function () { proxy.click(); }, hadPanel ? 380 : 0);
            return;
          }
        }

        e.preventDefault();
        lastFocus = document.activeElement;
        openPanel(key);
      });

      /* Sluiten via sluitknop (data-apdpc-sheet-close) of scrim-klik. */
      document.addEventListener('click', function (e) {
        if (e.target.closest ? e.target.closest('[data-apdpc-sheet-close]') : false) {
          closePanel();
          return;
        }
        if (e.target === scrim) {
          closePanel();
        }
      });

      /* Escape + focus-trap terwijl een paneel open is.
       * Focus-trap: Tab en Shift+Tab houden de focus in het actieve paneel.
       * Cyclisch: van laatste element terug naar eerste en omgekeerd.
       * Spiegelt exact de aricove-pdp-modal.liquid focus-trap (r.212-220). */
      document.addEventListener('keydown', function (e) {
        if (!activePanel) return;

        if (e.key === 'Escape') {
          closePanel();
          return;
        }

        if (e.key === 'Tab') {
          var f = focusables(activePanel);
          if (!f.length) return;
          var first = f[0];
          var last  = f[f.length - 1];

          if (e.shiftKey) {
            /* Shift+Tab op het eerste element: spring naar het laatste. */
            if (document.activeElement === first) {
              e.preventDefault();
              last.focus();
            }
          } else {
            /* Tab op het laatste element: spring terug naar het eerste. */
            if (document.activeElement === last) {
              e.preventDefault();
              first.focus();
            }
          }
        }
      });

  });

  /* ==================================================================
     TABS — snippets/aricove-pdp-c-tabs.liquid
     De DOMContentLoaded-wikkel is eruit: dit asset draait `defer`, dus de DOM
     staat er al. Bleef hij staan, dan bond een sectie die ná dat event binnenkomt
     nooit — precies het geval in de Theme Editor.
     ================================================================== */
  function bindTabs() {


      // Een controller voor alle [data-apdpc-tabs] op de pagina.
        document.querySelectorAll('[data-apdpc-tabs]').forEach(function (root) {
          /* P2: idempotent per wortel — herbinden mag geen tweede listenerset opleveren.
             Een PROPERTY, geen attribuut: innerHTML kopieert attributen wél mee, dus een
             attribuut zou een verse, ongebonden knoop als "al gebonden" bestempelen en de
             tabs in de Theme Editor doodleggen. Zie de toelichting bij layers. */
          if (root.__apdpcTabsBound) return;
          root.__apdpcTabsBound = true;
          var tabs   = Array.prototype.slice.call(root.querySelectorAll('[data-apdpc-tab]'));
          var panels = Array.prototype.slice.call(root.querySelectorAll('[data-apdpc-panel]'));

          function activate(idx) {
            tabs.forEach(function (t, i) {
              var active = i === idx;
              t.setAttribute('aria-selected', active ? 'true' : 'false');
              t.setAttribute('tabindex', active ? '0' : '-1');
            });
            panels.forEach(function (p, i) {
              if (i === idx) {
                p.removeAttribute('hidden');
              } else {
                p.setAttribute('hidden', '');
              }
            });
            tabs[idx].focus();
          }

          tabs.forEach(function (tab, idx) {
            tab.addEventListener('click', function () {
              activate(idx);
            });

            // Toetsenbord: pijltjes links/rechts, Home, End (roving tabindex).
            tab.addEventListener('keydown', function (e) {
              var key = e.key;
              var len = tabs.length;
              if (key === 'ArrowRight') {
                e.preventDefault();
                activate((idx + 1) % len);
              } else if (key === 'ArrowLeft') {
                e.preventDefault();
                activate((idx - 1 + len) % len);
              } else if (key === 'Home') {
                e.preventDefault();
                activate(0);
              } else if (key === 'End') {
                e.preventDefault();
                activate(len - 1);
              }
            });
          });
        });
  }
  module('__apdpcTabsBound', function () { /* geen eenmalige opbouw */ }, bindTabs);

  /* ==================================================================
     REVIEWOPBOUW — H26, sinds WILSON-BG 09-09 op alle viewports
     CSS zet de visuele volgorde direct goed. Deze controller verplaatst hetzelfde
     volledige Judge.me-sectionelement ook echt in de DOM, zodat focus- en leesvolgorde
     overeenkomen. Er wordt dus nooit een tweede Judge.me-widget geïnitialiseerd.
     ================================================================== */
  function plaatsVolledigeReviews() {
    var main = document.getElementById('MainContent');
    if (!main) return;
    var reviews = main.querySelector(':scope > .aricove-pdp-reviews');
    var highlights = main.querySelector(':scope > .aricove-pdp-review-highlights');
    var range = main.querySelector(':scope > .aricove-home-range, :scope > .aricove-pdp-range');
    if (!highlights) {
      main.classList.remove('has-apdp-review-highlights');
      return;
    }
    main.classList.add('has-apdp-review-highlights');
    if (!reviews || !range) return;

    /* WILSON-BG 09-09: review-highlights staan nu ook op desktop, dus het volledige
       Judge.me-component gaat op ÁLLE viewports vlak vóór Shop the range (CSS-order in
       aricove-pdp.css doet hetzelfde visueel; hier de DOM, zodat focus- en leesvolgorde kloppen). */
    if (reviews.nextSibling !== range) main.insertBefore(reviews, range);
    reviews.setAttribute('data-mobile-review-placed', 'true');
  }

  function bindReviewHighlights() {
    plaatsVolledigeReviews();
    document.querySelectorAll('[data-review-highlight-more]').forEach(function (btn) {
      if (btn.__apdpReviewMoreBound) return;
      btn.__apdpReviewMoreBound = true;
      btn.addEventListener('click', function () {
        var card = btn.closest('.review-highlight');
        if (!card) return;
        var open = !card.classList.contains('is-expanded');
        card.classList.toggle('is-expanded', open);
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        btn.textContent = open ? 'Read less' : 'Read more';
      });
    });
  }
  module('__apdpReviewHighlightsBound', function () { /* geen viewportafhankelijke opbouw meer (WILSON-BG 09-09) */ }, bindReviewHighlights);

  /* ==================================================================
     FAQ — sections/aricove-pdp-faq.liquid  (P1 tranche 1, 26-08)
     Eén single-open accordeoncontroller voor élke [data-apdp-faq] op de pagina.
     Verving zes identieke inline <script>-kopieën (WB, bed, bp, cover, fill) plus
     de <details>-toggle van kids-faq — CONTRACT §7b: eigen JS hoort in een asset.
     ================================================================== */
  function bindFaq() {
    document.querySelectorAll('[data-apdp-faq]').forEach(function (box) {
      /* 🟥 PROPERTY, geen attribuut. De Theme Editor vervangt een sectie via innerHTML
         en dát kopieert attributen mee: een `data-…-bound`-attribuut zou al op de verse,
         nog volstrekt ongebonden knoop zitten, de module slaat hem over en de accordeon
         is in de editor dood. Een property hangt aan het object, niet aan de markup. */
      if (box.__apdpFaqBound) return;
      box.__apdpFaqBound = true;

      box.querySelectorAll('.faq-item__btn').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var item = btn.closest('.faq-item');
          if (!item) return;
          var wasOpen = item.classList.contains('is-open');
          /* Single-open: eerst alles dicht binnen DEZE box (niet document-breed —
             twee FAQ-secties op één pagina mogen elkaar niet dichtklappen). */
          box.querySelectorAll('.faq-item.is-open').forEach(function (o) {
            o.classList.remove('is-open');
            var ob = o.querySelector('.faq-item__btn');
            if (ob) ob.setAttribute('aria-expanded', 'false');
          });
          if (!wasOpen) {
            item.classList.add('is-open');
            btn.setAttribute('aria-expanded', 'true');
          }
        });
      });
    });
  }
  module('__apdpFaqBound', function () { /* geen eenmalige opbouw */ }, bindFaq);

  /* ==================================================================
     WB RESEARCH-ACCORDEON — sections/aricove-pdp-research.liquid (H23)
     Drie gesloten regels, maximaal één open. Panels gebruiken `hidden`, zodat
     ingeklapte uitleg ook uit de accessibility tree blijft. De bestaande FAQ-
     controller gebruikt een CSS-max-height en past daarom niet op deze component.
     ================================================================== */
  function bindResearchAccordion() {
    document.querySelectorAll('[data-research-accordion]').forEach(function (box) {
      if (box.__apdpResearchAccordionBound) return;
      box.__apdpResearchAccordionBound = true;

      var buttons = box.querySelectorAll('[data-research-toggle]');
      function panelVoor(btn) {
        var id = btn.getAttribute('aria-controls');
        return id ? document.getElementById(id) : null;
      }
      function sluit(btn) {
        var card = btn.closest('.research-card--accordion');
        var panel = panelVoor(btn);
        btn.setAttribute('aria-expanded', 'false');
        if (card) card.classList.remove('is-open');
        if (panel) panel.hidden = true;
      }
      function open(btn) {
        var card = btn.closest('.research-card--accordion');
        var panel = panelVoor(btn);
        btn.setAttribute('aria-expanded', 'true');
        if (card) card.classList.add('is-open');
        if (panel) panel.hidden = false;
        /* REWORK WB-RV 09-09: de poster-video van deze regel laadt pas nu zijn eerste frame
           (src + preload=metadata); dichte regels laden niets. */
        if (panel) {
          panel.querySelectorAll('[data-research-video-poster]').forEach(function (v) {
            if (v.getAttribute('src')) return;
            v.setAttribute('preload', 'metadata');
            v.setAttribute('src', v.getAttribute('data-research-video-poster'));
            try { v.load(); } catch (e) { /* oude WebKit zonder load() */ }
          });
        }
      }

      /* 🟥 H23 was een MOBIELE inkorting; op desktop hoort de volle uitleg zichtbaar te zijn
         (BMDC 31-08). Vanaf 768px staan alle drie de panelen open en is de plus verborgen.
         Dat MOET hier gebeuren en niet alleen in CSS: de panelen worden met het
         `hidden`-attribuut gesloten, en een CSS-regel die daar `display:block` overheen zet
         maakt de tekst zichtbaar maar laat hem uit de accessibility tree — precies de fout
         die `hidden` hier juist moest voorkomen. `data-research-static` is de haak voor de
         bijbehorende CSS. */
      var mqDesktop = window.matchMedia('(min-width: 768px)');
      /* REWORK WB-RV 09-09 (BMDC-desktopmockup): een container met
         `data-research-desktop-accordion` blijft óók op desktop een accordeon, met de eerste
         regel open bij laden (daar zit het videoblok). Zonder attribuut: desktop alles open. */
      var desktopAccordeon = box.hasAttribute('data-research-desktop-accordion');
      function pasBreedteToe() {
        if (mqDesktop.matches && !desktopAccordeon) {
          box.setAttribute('data-research-static', '');
          buttons.forEach(open);
        } else {
          box.removeAttribute('data-research-static');
          buttons.forEach(sluit);
          if (mqDesktop.matches && buttons[0]) open(buttons[0]);
        }
      }
      if (mqDesktop.addEventListener) mqDesktop.addEventListener('change', pasBreedteToe);
      else if (mqDesktop.addListener) mqDesktop.addListener(pasBreedteToe);
      pasBreedteToe();

      buttons.forEach(function (btn) {
        btn.addEventListener('click', function () {
          if (mqDesktop.matches && !desktopAccordeon) return; /* desktop zonder optie: alles staat al open */
          var wasOpen = btn.getAttribute('aria-expanded') === 'true';
          buttons.forEach(sluit);
          if (!wasOpen) open(btn);
        });
      });
    });
  }
  module('__apdpResearchAccordionBound', function () { /* geen eenmalige opbouw */ }, bindResearchAccordion);

  /* ==================================================================
     FOOTER-ACCORDEON — sections/aricove-footer.liquid  (P2c, 27-08)
     Mobiel-only uitklap van de menukolommen; desktop toont ze al open.

     🟥 `document.currentScript.previousElementSibling` KON HIER NIET MEE.
        Inline stond de wortel via `currentScript` vast. In een asset is
        `currentScript` het asset zelf en is zijn buurman de eerstvolgende knoop in
        `theme.liquid` — niet de footer. De binding gaat daarom per KOP, en de kolom
        wordt via `closest('.footer-col')` gevonden: dat is dezelfde relatie die de
        oude code ook al gebruikte binnen de klik-handler.
     ================================================================== */
  function bindFooterAccordeon() {
    document.querySelectorAll('.footer-col__head').forEach(function (btn) {
      /* Property, geen attribuut — zelfde reden als bij de FAQ hierboven: de Theme
         Editor kopieert attributen mee bij een sectievervanging en zou de verse,
         ongebonden knoop dan overslaan. */
      if (btn.__apdpFooterBound) return;
      btn.__apdpFooterBound = true;

      btn.addEventListener('click', function () {
        /* Boven 769px doet de kop niets: de kolommen staan daar altijd open. Dezelfde
           grens als de CSS in de sectie; verandert die, dan verandert deze mee. */
        if (window.matchMedia('(min-width: 769px)').matches) return;
        var col = btn.closest('.footer-col');
        if (!col) return;
        var open = col.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    });
  }
  module('__apdpFooterAccBound', function () { /* geen eenmalige opbouw */ }, bindFooterAccordeon);

  /* ==================================================================
     HOME-RANGE "SHOW ALL" — sections/aricove-home-range.liquid  (P2c, 27-08)
     Klapt de productgrid uit en wisselt het knoplabel. Geen accordeon: er is één
     knop en één grid, en het label draagt het AANTAL, dus het moet exact herstellen.

     🟥 Het oorspronkelijke aantal komt uit `data-total` (door Liquid gezet), niet uit
        de gelezen labeltekst. Zou je de tekst onthouden, dan raakt hij bij een
        sectie-herbinding in de editor de verkeerde kant op.
     ================================================================== */
  function bindRangeMeer() {
    document.querySelectorAll('[data-range-more]').forEach(function (btn) {
      if (btn.__apdpRangeBound) return;
      btn.__apdpRangeBound = true;

      /* De grid hoort bij DEZE knop: zoek hem binnen de gedeelde sectie-wortel, niet
         document-breed. Twee range-secties op één pagina mogen elkaar niet bedienen. */
      var sec = btn.closest('.hp-range') || btn.closest('section') || document;
      var grid = sec.querySelector('[data-range-grid]');
      if (!grid) return;

      btn.addEventListener('click', function () {
        var open = grid.classList.toggle('is-expanded');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        btn.textContent = open ? 'Show less' : ('Show all ' + (btn.dataset.total || '') + ' products');
      });
    });
  }
  module('__apdpRangeBound', function () { /* geen eenmalige opbouw */ }, bindRangeMeer);

  /* ==================================================================
     PRESS-RAIL — sections/aricove-pdp-press.liquid  (P2c, 27-08)
     Horizontale rail met dots; onder 768px een scroller, daarboven een grid.

     🟥 `{{ section.id }}` IS HIER VERVALLEN. Inline koppelde de code rail aan dots via
        de in Liquid ingebakken sectie-id. Een asset heeft die waarde niet. De koppeling
        gaat nu via DOM-nabijheid: rail en dots dragen nog steeds hun id-attribuut, dus
        de dots worden gezocht op de WAARDE die op de rail staat. Dat is dezelfde
        koppeling, alleen uitgelezen in plaats van ingebakken — en het werkt ook als er
        twee press-secties op één pagina staan.

     🟥 Alleen actief onder 768px: boven die grens staan alle kaarten in beeld en zijn de
        dots `display:none`. Een observer die dan nog `aria-current` verzet is onzichtbaar
        maar niet gratis. De matchMedia-listener schakelt mee bij draaien.

     🟥 De dots zijn BEDIENBAAR, geen decoratie: echte <button>s, dus Enter/Space komen
        gratis mee via het klik-event. De rail zelf draagt `tabindex="0"` (A1-tranche 2);
        die staat in de markup en wordt hier niet aangeraakt.
     ================================================================== */
  function bindPressRail() {
    document.querySelectorAll('[data-press-track]').forEach(function (track) {
      if (track.__apdpPressBound) return;

      var sleutel = track.getAttribute('data-press-track');
      var dotsEl = document.querySelector('[data-press-dots="' + sleutel + '"]');
      if (!dotsEl) return;

      var dots = Array.prototype.slice.call(dotsEl.querySelectorAll('[data-press-dot]'));
      var items = Array.prototype.slice.call(track.querySelectorAll('.press-row__item'));
      if (!dots.length || !items.length) return;

      track.__apdpPressBound = true;

      var mq = window.matchMedia('(max-width: 767.98px)');
      var io = null;
      /* Zichtbaarheid per kaart; de dot volgt de LAAGSTE zichtbare index. */
      var zichtbaar = new Array(items.length).fill(false);

      function zetActief(pagina) {
        for (var i = 0; i < dots.length; i++) {
          var aan = i === pagina;
          dots[i].classList.toggle('is-active', aan);
          if (aan) dots[i].setAttribute('aria-current', 'true');
          else dots[i].removeAttribute('aria-current');
        }
      }

      function herbereken() {
        var eerste = zichtbaar.indexOf(true);
        if (eerste < 0) return;
        var pagina = Math.min(Math.floor(eerste / 2), dots.length - 1);
        zetActief(pagina);
      }

      function start() {
        if (io || !('IntersectionObserver' in window)) return;
        io = new IntersectionObserver(function (entries) {
          for (var i = 0; i < entries.length; i++) {
            var idx = items.indexOf(entries[i].target);
            if (idx >= 0) zichtbaar[idx] = entries[i].isIntersecting;
          }
          herbereken();
        }, { root: track, threshold: 0.6 });
        items.forEach(function (el) { io.observe(el); });
      }

      function stop() {
        if (!io) return;
        io.disconnect();
        io = null;
      }

      dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () {
          var doel = items[i * 2];
          if (!doel) return;
          var links = doel.offsetLeft - track.offsetLeft;
          var zacht = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          if (track.scrollTo) track.scrollTo({ left: links, behavior: zacht ? 'smooth' : 'auto' });
          else track.scrollLeft = links;
          zetActief(i);
        });
      });

      if (mq.matches) start();
      var onChange = function (e) { if (e.matches) start(); else stop(); };
      if (mq.addEventListener) mq.addEventListener('change', onChange);
      else if (mq.addListener) mq.addListener(onChange);
    });
  }
  module('__apdpPressBound', function () { /* geen eenmalige opbouw */ }, bindPressRail);

  /* ==================================================================
     CART — snippets/aricove-pdp-cart.liquid (add-to-cart-laag)
     Volledig gedelegeerd op document: knoppen uit later gerenderde secties
     zijn automatisch gedekt. Geen herbinding nodig, en dat is opzet.
     ================================================================== */
  module('__apdpCartBound', function () {

      function currentVariantId(btn){
        if(window.__apdpVariant && window.__apdpVariant.id) return window.__apdpVariant.id;
        return btn && btn.dataset ? btn.dataset.variantId : null;
      }

      document.addEventListener('click', function(e){
        var btn = e.target.closest ? e.target.closest('[data-apdp-atc]') : null;
        if(!btn || btn.disabled) return;
        e.preventDefault();
        var id = currentVariantId(btn);
        if(!id) return;
        var errorNode = btn.parentNode.querySelector('[data-apdp-cart-error]');
        if (errorNode) errorNode.remove();
        btn.disabled = true; btn.setAttribute('aria-busy','true');
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] })
        }).then(function(r){
          if(!r.ok) return r.json().catch(function(){ return {}; }).then(function(error){
            throw new Error(typeof error.description === 'string' ? error.description : 'Unable to add this item. Please try again.');
          });
          return r.json();
        }).then(function(data){
          document.dispatchEvent(new CustomEvent('aricove:cart-add-succeeded', {
            detail: { response: data }
          }));
          document.dispatchEvent(new CustomEvent('apdp:added', { detail: data }));
          btn.disabled = false; btn.removeAttribute('aria-busy');
          // If the slide-in drawer is present it handles the post-add UX (it opens on
          // apdp:added) — do NOT navigate. Fall back to /cart only when there's no drawer
          // (and the caller hasn't opted out via ApdpCartRedirect=false).
          var hasDrawer = !!document.querySelector('[data-apdp-drawer]');
          if(!hasDrawer && window.ApdpCartRedirect !== false){ window.location.href = '/cart'; }
        }).catch(function(err){
          if(window.console) console.warn('[apdp] ' + err.message);
          btn.disabled = !!(window.__apdpVariant && window.__apdpVariant.avail === false);
          btn.removeAttribute('aria-busy');
          // Keep the nested price span intact so a later variant change still updates it.
          var message = document.createElement('p');
          message.setAttribute('data-apdp-cart-error', '');
          message.setAttribute('role', 'alert');
          message.textContent = err.message || 'Unable to add this item. Please try again.';
          btn.insertAdjacentElement('afterend', message);
        });
      });
  });

  /* ==================================================================
     VIDEO — snippets/aricove-pdp-video-modal.liquid
     ================================================================== */
  module('__apdpVideoBound', function () {
      var modal = document.querySelector('[data-apdp-vmodal]');
      if(!modal) return;
      var frame = modal.querySelector('[data-apdp-vmodal-frame]');
      function embed(url){
        var u = (url || '').trim(); if(!u) return '';
        var yt = u.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
        if(yt) return '<iframe src="https://www.youtube.com/embed/' + yt[1] + '?autoplay=1&rel=0" allow="autoplay; fullscreen; encrypted-media" allowfullscreen></iframe>';
        var vm = u.match(/vimeo\.com\/(?:video\/)?(\d+)/);
        if(vm) return '<iframe src="https://player.vimeo.com/video/' + vm[1] + '?autoplay=1" allow="autoplay; fullscreen" allowfullscreen></iframe>';
        if(/\.(mp4|webm|mov)(\?|$)/i.test(u)) return '<video src="' + u + '" controls autoplay playsinline></video>';
        return '<iframe src="' + u + '" allow="autoplay; fullscreen" allowfullscreen></iframe>';
      }
      function open(url){ var html = embed(url); if(!html) return; frame.innerHTML = html; modal.hidden = false; void modal.offsetWidth; modal.classList.add('is-open'); document.body.style.overflow = 'hidden'; var c = modal.querySelector('[data-apdp-vmodal-close]'); if(c) setTimeout(function(){ c.focus(); }, 60); }
      function close(){ modal.classList.remove('is-open'); document.body.style.overflow = ''; setTimeout(function(){ modal.hidden = true; frame.innerHTML = ''; }, 250); }
      document.addEventListener('click', function(e){
        var t = e.target.closest ? e.target.closest('[data-apdp-video]') : null;
        if(t && t.getAttribute('data-video-url')){ e.preventDefault(); open(t.getAttribute('data-video-url')); }
      });
      modal.querySelectorAll('[data-apdp-vmodal-close]').forEach(function(b){ b.addEventListener('click', close); });
      document.addEventListener('keydown', function(e){ if(!modal.hidden && e.key === 'Escape') close(); });
  });

  /* ==================================================================
     HEADER — sections/aricove-header.liquid (cart-icoon + mobiele nav + announce)
     Alles is element-binding, dus in zijn geheel herbindbaar. Idempotent gemaakt met
     een `__acBound`-property op de knoop zelf (géén attribuut — zie layers): de header
     IS een sectie, dus dit is de module die in de Theme Editor het vaakst opnieuw
     langskomt, en juist hier zou een attribuut het cart-icoon doodleggen.
     ================================================================== */
  function bindHeader() {
      /* Cart icon opens the canonical drawer instead of navigating to /cart.
         (Wilson 06-25 Track 6 — catalog too small to justify a dedicated cart page.)
         The href stays as a no-JS fallback; JS intercepts and opens the drawer. */
      var cartBtn = document.querySelector('[data-apdp-cart-open]');
      if (cartBtn && cartBtn.__acBound) cartBtn = null;
      if(cartBtn && document.querySelector('[data-apdp-drawer]')){
        cartBtn.__acBound = true;
        cartBtn.addEventListener('click', function(e){
          e.preventDefault();
          document.dispatchEvent(new CustomEvent('apdp:cart:open'));
        });
      }
      /* Mobile nav toggle */
      var burger = document.querySelector('[data-ac-burger]');
      if (burger && burger.__acBound) burger = null;
      var mnav = document.querySelector('[data-ac-mobile-nav]');
      if(burger && mnav){
        burger.__acBound = true;
        burger.addEventListener('click', function(){
          var open = mnav.getAttribute('data-open') === 'true';
          var nextOpen = !open;
          mnav.setAttribute('data-open', nextOpen ? 'true' : 'false');
          mnav.setAttribute('aria-hidden', nextOpen ? 'false' : 'true');
          mnav.inert = !nextOpen;
          burger.setAttribute('aria-expanded', nextOpen ? 'true' : 'false');
        });
      }
      /* Announcement countdown + dismiss (only present when announce_on) */
      var bar = document.querySelector('[data-apdp-announce]');
      if (bar && bar.__acBound) bar = null;
      if(bar){
        bar.__acBound = true;
        var KEY = 'apdp-announce-dismissed';
        var dismissed = false; try { dismissed = !!sessionStorage.getItem(KEY); } catch(e){}
        if(!dismissed){
          var cd = bar.querySelector('[data-apdp-countdown]');
          var endIso = bar.getAttribute('data-sale-end');
          var end = endIso ? new Date(endIso).getTime() : NaN;
          var tick = function(){
            if(isNaN(end)){ if(cd) cd.hidden = true; return true; }
            var diff = end - Date.now();
            if(diff <= 0){ bar.hidden = true; return false; }
            var d = Math.floor(diff/86400000), h = Math.floor((diff/3600000)%24),
                m = Math.floor((diff/60000)%60), s = Math.floor((diff/1000)%60),
                p = function(n){ return (n<10?'0':'')+n; };
            if(cd){ cd.hidden = false; cd.innerHTML = '<span aria-hidden="true">&#9201;</span> ' + d + 'd : ' + p(h) + 'h : ' + p(m) + 'm : ' + p(s) + 's'; }
            return true;
          };
          if(tick()){
            bar.hidden = false;
            if(!isNaN(end)){ var iv = setInterval(function(){ if(!tick()) clearInterval(iv); }, 1000); }
          }
          var x = bar.querySelector('[data-apdp-announce-dismiss]');
          if(x) x.addEventListener('click', function(){ bar.hidden = true; document.documentElement.classList.add('apdp-announce-dismissed'); try { sessionStorage.setItem(KEY, '1'); } catch(e){} });
        }
      }
  }
  module('__apdpHeaderBound', function () { /* geen eenmalige opbouw */ }, bindHeader);

  /* ==================================================================
     THEME EDITOR — herbinding
     Shopify vervangt één sectie zonder paginarefresh. Alles wat aan een element in
     die sectie hing, hangt daarna aan een losse knoop. Draai daarom elke herbinder
     opnieuw; ze zijn stuk voor stuk idempotent.
     ================================================================== */
  document.addEventListener('shopify:section:load', function () {
    for (var i = 0; i < herbinders.length; i++) {
      try { herbinders[i](); } catch (e) {
        if (window.console) console.warn('[apdp] herbind: ' + e.message);
      }
    }
  });
})();
